import React, { useState, useEffect } from 'react'
import { timetableCCChoreography, timetableTinyTutus } from './TimetableConfig'
import PageWrapper from '../PageWrapper/PageWrapper'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import './TimetablePage.css'

const TimetablePage = () => {
  const [activeLocation, setActiveLocation] = useState('hamilton')
  const [selectedDay, setSelectedDay] = useState('Monday')
  
  // Use the appropriate timetable based on selected location
  // Fallback to CC Choreography timetable if Tiny Tutus timetable is not yet available
  const currentTimetable = activeLocation === 'hamilton' 
    ? timetableCCChoreography 
    : (timetableTinyTutus && timetableTinyTutus.length > 0 ? timetableTinyTutus : timetableCCChoreography)
  
  // Collect all unique days from all rows (not just the first row)
  const allDays = new Set()
  currentTimetable.forEach(row => {
    Object.keys(row).forEach(day => allDays.add(day))
  })
  const days = Array.from(allDays).sort((a, b) => {
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    return dayOrder.indexOf(a) - dayOrder.indexOf(b)
  })

  // Reset selected day when location changes
  useEffect(() => {
    if (days.length > 0 && !days.includes(selectedDay)) {
      setSelectedDay(days[0])
    }
  }, [activeLocation]) // eslint-disable-line react-hooks/exhaustive-deps

  // Get abbreviated day name for mobile buttons (medium screens)
  const getDayAbbreviation = (day) => {
    const abbreviations = {
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    }
    return abbreviations[day] || day.slice(0, 3)
  }

  // Get single letter abbreviation for small screens
  const getDayLetter = (day) => {
    const letters = {
      'Monday': 'M',
      'Tuesday': 'T',
      'Wednesday': 'W',
      'Thursday': 'Th',
      'Friday': 'F',
      'Saturday': 'S',
      'Sunday': 'Su'
    }
    return letters[day] || day[0]
  }

  // Transform timetable data for mobile card view (group by day)
  const getClassesByDay = (day) => {
    const classes = []
    currentTimetable.forEach((row) => {
      if (row[day] && row[day]['Class']) {
        const cellData = row[day]
        // Check if it's an array of multiple lessons
        if (Array.isArray(cellData['Class'])) {
          // Add all lessons from the array
          cellData['Class'].forEach(lesson => {
            if (lesson.class && lesson.class.trim() !== '') {
              classes.push({
                class: lesson.class,
                time: lesson.time
              })
            }
          })
        } else if (cellData['Class'].trim() !== '') {
          // Single lesson
          classes.push({
            class: cellData['Class'],
            time: cellData['Time']
          })
        }
      }
    })
    return classes
  }

  const locationIntro =
    activeLocation === 'hamilton'
      ? 'Kids and teen dance classes at our Hamilton North studio (Newcastle NSW) — ballet, jazz, hip hop, acro, troupe and more.'
      : 'Preschool and kids dance classes at Tiny Tutus, Lambton — including Lil Groovers for ages 2–5.'

  return (
    <PageWrapper title='2026 Timetable'>
      <p className='timetable-intro'>{locationIntro}</p>
      {/* Location Tabs */}
      <div className='timetable-location-tabs'>
        <button 
          className={`location-tab ${activeLocation === 'hamilton' ? 'active' : ''}`}
          onClick={() => setActiveLocation('hamilton')}
        >
          <span className='location-name'>CC Choreography</span>
          <span className='location-subtitle'>Hamilton North</span>
        </button>
        <button 
          className={`location-tab ${activeLocation === 'lambton' ? 'active' : ''}`}
          onClick={() => setActiveLocation('lambton')}
        >
          <span className='location-name'>Tiny Tutus</span>
          <span className='location-subtitle'>Lambton</span>
        </button>
      </div>
      
      {/* Desktop Table View */}
      <div className='timetable-desktop'>
        <PerfectScrollbar>
          <table className='timetable'>
            <thead>
              <tr>
                {days.map(day =>
                  <th key={day}>{day}</th>
                )}
              </tr>
            </thead>

            {currentTimetable.map((row, key) =>
              <tbody key={key}>
                <tr>
                  {days.map(day => {
                    const cellData = row[day]
                    
                    // If day doesn't exist in this row, render empty cell
                    if (!cellData) {
                      return <td key={day}></td>
                    }
                    
                    const isMultipleLessons = Array.isArray(cellData['Class'])
                    
                    return (
                      <td key={day} className={isMultipleLessons ? 'has-multiple-lessons' : ''}>
                        {isMultipleLessons ? (
                          // Multiple lessons - render stacked
                          cellData['Class'].map((lesson, lessonIndex) => (
                            <div key={lessonIndex} className='timetable-lesson'>
                              <div className='timetable-class'>{lesson.class}</div>
                              <div className='timetable-time'>{lesson.time}</div>
                            </div>
                          ))
                        ) : (
                          // Single lesson - render normally
                          <>
                            <div className='timetable-class'>{cellData['Class']}</div>
                            <div className='timetable-time'>{cellData['Time']}</div>
                          </>
                        )}
                      </td>
                    )
                  })}
                </tr>
              </tbody>
              )}
          </table>
        </PerfectScrollbar>
      </div>

      {/* Mobile Card View */}
      <div className='timetable-mobile'>
        {/* Day Selector */}
        <div className='day-selector'>
          {days.map(day => (
            <button
              key={day}
              className={`day-button ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              <span className='day-abbreviation'>{getDayAbbreviation(day)}</span>
              <span className='day-letter'>{getDayLetter(day)}</span>
            </button>
          ))}
        </div>

        {/* Classes for Selected Day */}
        <div className='day-classes'>
          <h3 className='day-title'>{selectedDay}</h3>
          {getClassesByDay(selectedDay).length > 0 ? (
            <div className='class-cards'>
              {getClassesByDay(selectedDay).map((classItem, index) => (
                <div key={index} className='class-card'>
                  <div className='class-time'>{classItem.time}</div>
                  <div className='class-name'>{classItem.class}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className='no-classes'>No classes scheduled for {selectedDay}</div>
          )}
        </div>
      </div>

      {/* <div className='sub-timetable-info-wrapper'>
        <div className='red-text'>Term 1 Starts:</div>
        <div className='sub-timetable-info'>
          Friday 29th January - Thursday 1st April (9 week term)
        </div>
      </div> */}
    </PageWrapper>
  )
}

export default TimetablePage